/* istanbul ignore file */
import { lazy, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import type { UnicornApi } from '@cld/unicorn-api';

import { RemoteModule } from '../../components/remote-module/remote-module';
import { modules } from '../../components/side-menu/side-menu.constants';
import { useMessage } from '../../i18n/useMessage';
import { useExtractTransformationsFromUrl } from './hooks/extractTransformationsFromUrl';
import { useInitialSignature } from './hooks/initialSignature';

type Capability = UnicornApi['data']['capabilities'][number];
type TransformationSuiteModule = Awaited<typeof import('@cld-remote/transformation_suite/index')>;

const FixedWrapper = styled.div`
  height: 100vh;
  width: 100vw;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 5;
`;

const TransformationSuite = lazy(() =>
  import('@cld-remote/transformation_suite/index').then(({ TransformationSuite }: TransformationSuiteModule) => ({
    default: TransformationSuite,
  }))
);

type TransformationSuiteFeatureFlag = 'ai_assistant' | 'ai_transformation_refinement';

const AI_ASSISTANT_ENABLED: Capability = 'transformationBuilderAiAssistantEnabled';
const AI_TRANSFORMATION_REFINEMENT: Capability = 'aiTransformationRefinement';

const ROLLBAR_ACCESS_TOKEN = '39575b31ed934c8a9d00ee98849fcc58';

export interface TransformationSuiteProps {
  unicornApi: UnicornApi;
  shouldDisplayThumbnails?: boolean;
}

export default function ({ unicornApi, shouldDisplayThumbnails }: TransformationSuiteProps) {
  const { capabilities, productEnvironment, user, theme } = unicornApi.data;

  const t = useMessage();
  const navigate = useNavigate();
  // eslint-disable-next-line react-hooks/exhaustive-deps, @typescript-eslint/unbound-method
  const authorizedFetch = useMemo(() => unicornApi.methods.createFetchClient(), [unicornApi.methods.createFetchClient]);

  const { transformationString, mediaType, galleryMode, assetSetId } = useExtractTransformationsFromUrl();
  const initialSignature = useInitialSignature();

  // Create dedicated Rollbar error tracker
  const ErrorTracker = useMemo(() => unicornApi.methods.createLogger(ROLLBAR_ACCESS_TOKEN), [unicornApi.methods.createLogger]);

  const isAiAssistantEnabled = capabilities.includes(AI_ASSISTANT_ENABLED);
  const isAiTransformationRefinementEnabled = capabilities.includes(AI_TRANSFORMATION_REFINEMENT);

  const featureFlags: TransformationSuiteFeatureFlag[] = [];

  if (isAiAssistantEnabled) {
    featureFlags.push('ai_assistant');
  }

  if (isAiTransformationRefinementEnabled) {
    featureFlags.push('ai_transformation_refinement');
  }

  const onNamedTransformationClose = () => navigate(`../${modules.transformationsManage}`);

  return (
    <RemoteModule documentTitle={t('transformation-suite')}>
      {createPortal(
        <ErrorTracker.ErrorBoundary errorBoundaryLogLabel="Transformation Suite">
          <FixedWrapper>
            <TransformationSuite
              unicornApi={unicornApi}
              theme={theme.name}
              user={user}
              authorizedFetch={authorizedFetch}
              apiSecret={productEnvironment.apiSecret || ''} // TODO: What fallback should there be if we don't have api_secret? @mckomo-cl
              cloudName={productEnvironment.cloudName}
              mediaType={mediaType === 'video' ? 'video' : 'image'}
              strictTransformationsEnabled={productEnvironment.strictTransformations}
              initialTransformationString={transformationString}
              initialTransformationSignature={initialSignature}
              contextFeatureFlags={featureFlags}
              localStoragePrefix="@cld/transformation-suite"
              onNamedTransformationClose={onNamedTransformationClose}
              shouldDisplayThumbnails={shouldDisplayThumbnails}
              initialGalleryMode={galleryMode}
              assetSetId={assetSetId}
            />
          </FixedWrapper>
        </ErrorTracker.ErrorBoundary>,
        document.body
      )}
    </RemoteModule>
  );
}
